/**
 * Installing Aliens OS as an app (PWA). Chrome / Edge / Android fire `beforeinstallprompt` once, early, so it is
 * captured at startup (this module is imported by main.tsx) and replayed when the person presses "Install".
 * iPhone Safari has no prompt: the page explains Share → Add to Home Screen instead.
 */
import { useSyncExternalStore } from 'react'

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: InstallPromptEvent | null = null
let installed = false
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

export const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || window.matchMedia?.('(display-mode: window-controls-overlay)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true)

export const isIOS = () => typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios|edgios/i.test(navigator.userAgent)

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferred = e as InstallPromptEvent
    emit()
  })
  window.addEventListener('appinstalled', () => {
    installed = true
    deferred = null
    emit()
  })
  if (isStandalone()) document.documentElement.classList.add('is-standalone')
}

export type InstallState = 'installed' | 'prompt' | 'ios' | 'unavailable'
const snapshot = (): InstallState => (installed || isStandalone() ? 'installed' : deferred ? 'prompt' : isIOS() ? 'ios' : 'unavailable')

export function useInstallState() {
  return useSyncExternalStore((cb) => (listeners.add(cb), () => listeners.delete(cb)), snapshot, () => 'unavailable' as InstallState)
}

/** Shows the browser's own install dialog; resolves true when the person accepted. */
export async function promptInstall() {
  if (!deferred) return false
  const e = deferred
  deferred = null
  await e.prompt()
  const { outcome } = await e.userChoice
  emit()
  return outcome === 'accepted'
}
